import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureConfigContext } from '@/contexts/FeatureConfigContext';
import { useAppState } from '@/contexts/AppStateContext';
import { SubscriptionTier } from '@everyday-os/shared';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { MaintenanceBanner } from './MaintenanceBanner';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';

interface FeatureGuardProps {
  featureId: string;
  children: React.ReactNode;
}

export const FeatureGuard: React.FC<FeatureGuardProps> = ({ featureId, children }) => {
  const { t } = useTranslation();
  const { configs } = useFeatureConfigContext();
  // Use the hook — throws a descriptive error if used outside AppStateContext.Provider
  const { state } = useAppState();
  const household = state?.households?.find(h => h.id === state.currentHouseholdId);
  const householdTier = (household?.subscription?.tier ?? SubscriptionTier.FREE) as SubscriptionTier;
  const hiddenFeatures = state?.currentUser?.hiddenFeatures ?? [];
  const householdModules = household?.subscription?.enabledAddOns ?? [];

  const { isAccessible, isInMaintenance, isDisabled } = useFeatureAccess(
    householdTier,
    hiddenFeatures,
    {
      householdId: household?.id,
      householdModules,
    }
  );

  const config = configs.get(featureId);
  const disabled = isDisabled(featureId);
  const inaccessible = !disabled && !isAccessible(featureId);

  useEffect(() => {
    if (disabled) {
      showToast(t('feature_guard.feature_disabled', 'This feature is currently unavailable.'), 'info');
    } else if (inaccessible) {
      showToast(t('feature_guard.feature_locked', 'This feature requires a higher subscription plan.'), 'info');
    }
  }, [disabled, inaccessible]);

  if (disabled) {
    return <Navigate to={config?.disabledRedirectPath || '/dashboard'} replace />;
  }

  if (inaccessible) {
    return <Navigate to="/explore" replace />;
  }

  if (isInMaintenance(featureId)) {
    return (
      <>
        <MaintenanceBanner featureId={featureId} />
        {children}
      </>
    );
  }

  return <>{children}</>;
};
